import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpHandler, ɵHttpInterceptingHandler } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, inject, Component, Directive, Input, APP_INITIALIZER, ENVIRONMENT_INITIALIZER, NgModule } from '@angular/core';
import * as i2 from '@angular/router';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, of, from, firstValueFrom, EMPTY, combineLatest, switchMap as switchMap$1, tap as tap$1, first } from 'rxjs';
import * as i1 from '@azure/msal-angular';
import { MsalCustomNavigationClient, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalGuard, MsalBroadcastService, MsalService, MsalInterceptor, MSAL_INSTANCE, MsalModule } from '@azure/msal-angular';
import { InteractionStatus, EventType, PublicClientApplication, BrowserCacheLocation, InteractionType } from '@azure/msal-browser';
import { WINDOW, LOCATION } from '@mercadona/core/utils/tokens';
import { map, switchMap, catchError, tap, filter, takeUntil, delay, finalize, take } from 'rxjs/operators';
import { Buffer } from 'buffer';
import { coerceStringArray } from '@angular/cdk/coercion';

const M_AUTH_CONFIGURATION = new InjectionToken('M_AUTH_CONFIGURATION');
const M_AUTH_INJECTION_LEVEL = new InjectionToken('M_AUTH_INJECTION_LEVEL');
const M_CALLBACK_ROUTE = 'callback';

/**
 *
 * @param {string} plain A string to encode
 * @returns {Promise<ArrayBuffer>} A promise
 */
function sha256(plain) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest('SHA-256', data);
}
/**
 *
 * @param {ArrayBuffer} a An array buffer
 * @returns {string} An encode string
 */
function base64urlencode(a) {
    let result = '';
    const bytes = new Uint8Array(a);
    for (let i = 0; i < bytes.byteLength; i++) {
        result += String.fromCharCode(bytes[i]);
    }
    return window.btoa(result).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/**
 *
 * @param {string} codeVerifier The code
 * @returns {Promise<string>} A promise with the code generated
 */
async function generateCodeChallenge(codeVerifier) {
    const hashed = await sha256(codeVerifier);
    const encoded = base64urlencode(hashed);
    return encoded;
}
/**
 *@returns {string} A random string
 */
function generateRandomString() {
    const dec2hex = (d) => Number(d).toString(16).padStart(2, '0');
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec2hex).join('');
}
/**
 *
 * @template T Generic
 * @param {string} jwt An encoded token string
 * @returns {T | null} The token decoded
 */
function decodeToken(jwt) {
    try {
        return JSON.parse(Buffer.from(jwt.split('.')[1], 'base64').toString('utf-8'));
    }
    catch (err) {
        return null;
    }
}
const validateToken = (token) => {
    if (!token || token === 'null') {
        return false;
    }
    const tokenDecoded = decodeToken(token);
    if (!tokenDecoded) {
        return false;
    }
    const isValid = Object.keys(tokenDecoded).length !== 0;
    return Boolean(isValid);
};

class MAuthService {
    constructor(_mAuthConfig, _msalGuardConfig, _msalInterceptorConfiguration, window, broadcastService, msalService, router, location) {
        this._mAuthConfig = _mAuthConfig;
        this._msalGuardConfig = _msalGuardConfig;
        this._msalInterceptorConfiguration = _msalInterceptorConfiguration;
        this.window = window;
        this.broadcastService = broadcastService;
        this.msalService = msalService;
        this.router = router;
        this.location = location;
        this._authenticationContext$ = new BehaviorSubject(null);
        this._tokenContext$ = new BehaviorSubject(null);
        this.destroy$ = new Subject();
        this.isAuthenticated$ = this._authenticationContext$
            .asObservable()
            .pipe(map((payload) => Boolean(payload)));
        this.user$ = this._authenticationContext$.asObservable();
        this.token$ = this._tokenContext$.asObservable().pipe(switchMap((token) => {
            if (token === null) {
                return this.isAuthenticated$.pipe(switchMap((isAuthenticated) => (isAuthenticated ? this.getToken() : of(null))));
            }
            return of(token);
        }));
        this._loaded$ = new Subject();
        this.loaded$ = this._loaded$.asObservable();
        this._load();
    }
    get mAuthConfig() {
        return this._mAuthConfig;
    }
    get msalGuardConfig() {
        return this._msalGuardConfig;
    }
    get msalInterceptorConfiguration() {
        return this._msalInterceptorConfiguration;
    }
    get authenticationContext() {
        return this._authenticationContext$.asObservable();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this._loaded$.complete();
    }
    /**
     * Call the authentication server login with default redirect method.
     * If a loginHint is provided, it will be logged with SSO silent.
     *
     * @param {string} loginHint A string with the user email to log in
     * @param {boolean} force Force authentication prompt when accessToken is expired
     * @returns {Observable<void | AuthenticationResult>} An observable with the authentication result
     */
    login(loginHint, force) {
        if (loginHint) {
            const silentRequest = {
                scopes: { ...this.msalGuardConfig.authRequest }.scopes,
                prompt: { ...this.msalGuardConfig.authRequest }.prompt,
                authority: this.mAuthConfig.authority,
                redirectUri: this.mAuthConfig.redirectUri,
                loginHint
            };
            return this.msalService.ssoSilent(silentRequest).pipe(catchError(() => this._loginRedirect(force)));
        }
        return this._loginRedirect(force);
    }
    /**
     * Call the authentication server login with popup method
     *
     * @param  {boolean} force Force authentication prompt when accessToken is expired
     * @returns {Observable<AuthenticationResult>} An observable with the authentication result
     */
    loginPopup(force) {
        if (this.msalGuardConfig.authRequest) {
            return this.msalService
                .loginPopup({
                ...this.msalGuardConfig.authRequest,
                prompt: force ? 'login' : this.msalGuardConfig.authRequest.prompt
            })
                .pipe(tap({
                next: (response) => {
                    this.msalService.instance.setActiveAccount(response.account);
                }
            }));
        }
        return this.msalService.loginPopup(force ? { prompt: 'login' } : undefined).pipe(tap({
            next: (response) => {
                this.msalService.instance.setActiveAccount(response.account);
            }
        }));
    }
    /**
     * Call the authentication server logout
     *
     * @returns {Observable<void>} A void observable
     */
    logout() {
        const idToken = this._tokenContext$.value?.idToken;
        this.window.sessionStorage.setItem('forceAuthentication', 'true');
        if (!idToken) {
            return from(firstValueFrom(this.getToken()).then(() => {
                this._checkRedirectInteraction(() => setTimeout(() => this.msalService.logoutRedirect({ idTokenHint: this._tokenContext$.value?.idToken || '' })));
            }));
        }
        return this._checkRedirectInteraction(() => setTimeout(() => this.msalService.logoutRedirect({ idTokenHint: this._tokenContext$.value?.idToken || '' })));
    }
    /**
     * Get the tokens by scope.
     *
     * If no token is cached, or it is expired, request new one.
     *
     * @param {string} scope Scope value used in the module configuration
     * @returns {Observable<MAuthenticationResult>} Tokens by the specific scope
     */
    getToken(scope) {
        let activeAccount = this.msalService.instance.getActiveAccount();
        if (!activeAccount) {
            if (this.msalService.instance.getAllAccounts().length > 0) {
                const accounts = this.msalService.instance.getAllAccounts();
                activeAccount = accounts[0];
                this.msalService.instance.setActiveAccount(activeAccount);
            }
            else {
                this.login();
            }
            return EMPTY;
        }
        let scopes;
        if (scope) {
            if (typeof scope === 'string') {
                scopes = [scope];
            }
            else {
                if (scope.length > 0) {
                    scopes = [...scope];
                }
            }
        }
        const accessTokenRequest = {
            scopes: scopes ? scopes : { ...this.msalGuardConfig.authRequest }.scopes,
            account: activeAccount
        };
        try {
            return this.msalService
                .acquireTokenSilent(accessTokenRequest)
                .pipe(catchError(() => this._catchErrorGetToken(accessTokenRequest.account, accessTokenRequest.scopes)));
        }
        catch (error) {
            return this._catchErrorGetToken(accessTokenRequest.account, accessTokenRequest.scopes);
        }
    }
    _catchErrorGetToken(activeAccount, scopes) {
        const request = {
            ...this.msalGuardConfig.authRequest,
            account: activeAccount,
            scopes: scopes ? scopes : { ...this.msalGuardConfig.authRequest }.scopes,
            loginHint: activeAccount?.username,
            redirectStartPage: this._mAuthConfig.navigateToLoginRequestUrl
                ? window.location.href
                : this._mAuthConfig.redirectUri,
            prompt: 'login'
        };
        this.msalService.acquireTokenRedirect(request);
        return EMPTY;
    }
    /**
     * Picks up the roles according to the scope provided
     *
     * @param {string} scope A scope for an audience
     * @returns {Observable<string[]>} An observable with a string array
     */
    getPermissions(scope) {
        return this.getToken(scope).pipe(map((response) => this._getRoles(response)));
    }
    /**
     * Checks if the user has permissions or not according to the provided roles
     *
     * @param {string | string[]} roles The roles to check with the user
     * @param {string} scope The audience to match the permission with roles
     * @returns {Observable<boolean>} A boolean observable
     */
    hasPermission(roles, scope) {
        const levels = Array.isArray(roles) ? roles : [roles];
        return this.getPermissions(scope).pipe(map((permissions) => levels.every((v) => permissions.includes(v))));
    }
    /**
     * Checks if the user has some permissions or not according to the provided roles
     *
     * @param {string[]} roles The roles to check with the user
     * @param {string} scope The audience to match the permission with roles
     * @returns {Observable<boolean>} A boolean observable
     */
    hasSomePermission(roles, scope) {
        return this.getPermissions(scope).pipe(map((permissions) => roles.some((v) => permissions.includes(v))));
    }
    /**
     * Collects the resources
     *
     * @returns {Map<string, (string | MProtectedResourceScopes)[] | null> } A map of resources
     */
    getRestServices() {
        return this.msalInterceptorConfiguration.protectedResourceMap;
    }
    /**
     * Clears the resources map and adds the new array to the map
     *
     * @param {MRESTService[]} restServices An array of resources
     */
    setRestServices(restServices) {
        this.msalInterceptorConfiguration.protectedResourceMap.clear();
        let protectedResourceMap = new Map();
        if (restServices) {
            protectedResourceMap = configureResourceMap(restServices);
        }
        this.msalInterceptorConfiguration.protectedResourceMap = protectedResourceMap;
    }
    /**
     * Clears the resources map
     */
    clearRestServices() {
        this.setRestServices([]);
    }
    /**
     * Adds a resource to the resources map. If there is the same resource, no one is added
     *
     * @param {MRESTService} restService A single resource (MRESTService)
     */
    addRestService(restService) {
        this.msalInterceptorConfiguration.protectedResourceMap.set(restService.url, restService.scope.length > 0
            ? restService.scope.map((scp) => (restService.resource ? restService.resource + '/' + scp : scp))
            : restService.resource
                ? [restService.resource]
                : null);
    }
    _getRoles(response) {
        if (response.accessTokenClaims) {
            const roles = response.accessTokenClaims.roles ||
                response.accessTokenClaims.operationGroups ||
                response.accessTokenClaims.operationgroups ||
                response.accessTokenClaims.extension_Groups;
            return roles ? roles.split(';') : [];
        }
        return [];
    }
    _manageInProgressSubject() {
        return this.broadcastService.inProgress$.pipe(filter((status) => status === InteractionStatus.None), tap({
            next: () => this._refreshAuthUser()
        }), takeUntil(this.destroy$));
    }
    _manageMsalSubject() {
        return this.broadcastService.msalSubject$.pipe(filter((status) => status.eventType === EventType.LOGIN_SUCCESS || status.eventType === EventType.ACQUIRE_TOKEN_SUCCESS), tap({
            next: (status) => {
                const payload = status.payload;
                if (payload && payload.accessToken) {
                    payload.accessTokenClaims = decodeToken(payload.accessToken) || {};
                }
                this._tokenContext$.next(payload);
            }
        }), takeUntil(this.destroy$));
    }
    /**
     * We can navigate to error page when token fails.
     * For instance, an audience from ADFS that we use, and it is not in AZURE has to throw error.
     *
     * @returns {Observable<EventMessage>} An observable from msalSubject$
     * @private
     */
    _manageAcquireTokenError() {
        return this.broadcastService.msalSubject$.pipe(filter((status) => status.eventType === EventType.ACQUIRE_TOKEN_FAILURE), delay(0), tap({
            next: (result) => {
                void this.router.navigate(['error'], {
                    state: {
                        title: 'm-page-error.title',
                        subtitle: 'm-page-error.subtitle',
                        message: 'm-page-error.message',
                        detail: result.error || result
                    }
                });
            }
        }), takeUntil(this.destroy$));
    }
    /**
     * Forces to clear when interaction_in_progress blocks the user flow in redirect interactions
     *
     * @param {() => void} fn The redirect function
     * @returns {Observable<void>} A void observable
     */
    _checkRedirectInteraction(fn) {
        if (!this.msalService.instance.getActiveAccount()) {
            const browserCacheManage = this.msalService.instance.browserStorage;
            void browserCacheManage.clear();
            // eslint-disable-next-line no-console
            console.warn('[Auth] Clear browser manager cache by interaction in progress');
        }
        return of(fn());
    }
    _load() {
        this.msalService
            .handleRedirectObservable()
            .pipe(tap({
            next: (result) => {
                if (result) {
                    this.msalService.instance.setActiveAccount(result.account);
                }
            },
            error: (result) => {
                void this.router.navigate(['error'], {
                    state: {
                        title: 'm-page-error.title',
                        subtitle: 'm-page-error.subtitle',
                        message: 'm-page-error.message',
                        detail: result
                    }
                });
            }
        }), finalize(() => {
            this._loaded$.next();
            const customNavigationClient = new MsalCustomNavigationClient(this.msalService, this.router, this.location);
            this.msalService.instance.setNavigationClient(customNavigationClient);
        }), takeUntil(this.destroy$))
            .subscribe();
        this.loaded$
            .pipe(switchMap(() => combineLatest([this._manageInProgressSubject(), this._manageMsalSubject(), this._manageAcquireTokenError()])))
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }
    _loginRedirect(force) {
        force = force ?? Boolean(this.window.sessionStorage.getItem('forceAuthentication'));
        return this._checkRedirectInteraction(() => setTimeout(() => {
            if (this.msalGuardConfig.authRequest) {
                return this.msalService.loginRedirect({
                    ...this.msalGuardConfig.authRequest,
                    prompt: force ? 'login' : this.msalGuardConfig.authRequest.prompt
                });
            }
            return this.msalService.loginRedirect(force ? { prompt: 'login' } : undefined);
        })).pipe(delay(250));
    }
    _refreshAuthUser() {
        let activeAccount = this.msalService.instance.getActiveAccount();
        if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
            const accounts = this.msalService.instance.getAllAccounts();
            activeAccount = accounts[0];
            this.msalService.instance.setActiveAccount(activeAccount);
        }
        if (activeAccount) {
            const activeAccountConst = activeAccount;
            this.getToken()
                .pipe(take(1), tap((token) => {
                const roles = this._getRoles(token);
                this._authenticationContext$.next({
                    id: activeAccountConst.homeAccountId,
                    email: activeAccountConst.idTokenClaims?.emails?.[0] ||
                        token.accessTokenClaims?.upn ||
                        token.accessTokenClaims?.preferred_username ||
                        token.accessTokenClaims?.email ||
                        '',
                    roles: roles.length > 0 ? roles : this._getRolesFromUser(activeAccountConst.idTokenClaims),
                    username: activeAccountConst.username
                });
            }))
                .subscribe();
        }
        else {
            this._authenticationContext$.next(null);
        }
    }
    _getRolesFromUser(idTokenClaims) {
        return idTokenClaims?.roles || idTokenClaims?.roles || [];
    }
}
MAuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthService, deps: [{ token: M_AUTH_CONFIGURATION }, { token: MSAL_GUARD_CONFIG }, { token: MSAL_INTERCEPTOR_CONFIG }, { token: WINDOW }, { token: i1.MsalBroadcastService }, { token: i1.MsalService }, { token: i2.Router }, { token: i3.Location }], target: i0.ɵɵFactoryTarget.Injectable });
MAuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [M_AUTH_CONFIGURATION]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MSAL_GUARD_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MSAL_INTERCEPTOR_CONFIG]
                }] }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1.MsalBroadcastService }, { type: i1.MsalService }, { type: i2.Router }, { type: i3.Location }]; } });

class MAuthCallbackComponent {
    constructor() {
        this.router = inject(Router);
        this.mAuthService = inject(MAuthService);
    }
    ngOnInit() {
        if (!this.mAuthService.mAuthConfig?.navigateToLoginRequestUrl) {
            void this.router.navigateByUrl('/', {
                replaceUrl: true
            });
        }
    }
}
MAuthCallbackComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthCallbackComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MAuthCallbackComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: MAuthCallbackComponent, isStandalone: true, selector: "ng-component", ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthCallbackComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                    standalone: true
                }]
        }] });

const loggerOptions = {
    loggerCallback: (logLevel, message) => {
        if (logLevel === 0) {
            // eslint-disable-next-line no-console
            console.error(message);
        }
        else {
            // eslint-disable-next-line no-console
            console.log(message);
        }
    },
    piiLoggingEnabled: true
};
const msalInstanceFactory = (mAuthConfig) => {
    const knownAuthorities = new Set(mAuthConfig.knownAuthorities || []);
    if (mAuthConfig.authority) {
        knownAuthorities.add(mAuthConfig.authority);
    }
    //TODO: Add the default Authoritie for emergency
    // knownAuthorities.add('https://emergency.example.com/adfs');, should modify test
    return new PublicClientApplication({
        auth: {
            clientId: mAuthConfig.clientId,
            authority: mAuthConfig.authority,
            knownAuthorities: Array.from(knownAuthorities),
            redirectUri: mAuthConfig.redirectUri,
            postLogoutRedirectUri: mAuthConfig.postLogoutRedirectUri,
            authorityMetadata: mAuthConfig.authorityMetadata,
            protocolMode: mAuthConfig.protocolMode,
            navigateToLoginRequestUrl: mAuthConfig.navigateToLoginRequestUrl,
            cloudDiscoveryMetadata: mAuthConfig.cloudDiscoveryMetadata
        },
        cache: {
            cacheLocation: mAuthConfig.cacheLocation || BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false,
            secureCookies: true
        },
        system: {
            loggerOptions: mAuthConfig.logChanges ? loggerOptions : {}
        }
    });
};
const msalGuardConfigFactory = (mAuthConfig) => ({
    interactionType: InteractionType.Redirect,
    authRequest: {
        scopes: _getScopesAuthRequest(mAuthConfig.scope, mAuthConfig.resource),
        // Prompt behaviour is specified in https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-prompt-behavior
        prompt: mAuthConfig.forceAuthentication ? 'login' : undefined
    },
    loginFailedRoute: mAuthConfig.loginFailedRoute
});
const msalInterceptorConfigFactory = (mAuthConfig) => {
    let protectedResourceMap = new Map();
    if (mAuthConfig.restServices) {
        protectedResourceMap = configureResourceMap(mAuthConfig.restServices);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
};
const configureResourceMap = (services) => {
    const protectedResourceMap = new Map();
    services.map((service) => {
        let scope;
        if (!service.resource) {
            scope = null;
        }
        else {
            if (service.scope.length !== 0) {
                scope = service.scope.map((scp) => {
                    if (Object.prototype.hasOwnProperty.call(scp, 'httpMethod')) {
                        const resources = scp;
                        const scopes = _getScopes(service, resources);
                        const httpMethod = resources.httpMethod;
                        return { scopes, httpMethod };
                    }
                    return service.resource + '/' + scp;
                });
            }
            else {
                scope = [service.resource];
            }
        }
        protectedResourceMap.set(service.url, scope);
    });
    return protectedResourceMap;
};
/**
 *
 * @param {MRESTService} service The rest services
 * @param {MProtectedResourceScopes} resources The auth resources declared
 * @returns {(string | null)[] | null} The scopes
 */
function _getScopes(service, resources) {
    if (resources.scopes) {
        if (resources.scopes.length !== 0) {
            return resources.scopes.map((scp) => service.resource + '/' + scp);
        }
        return [service.resource];
    }
    return null;
}
/**
 * @param {string[] | undefined} scopes The scopes
 * @param {string | undefined} resource The resource
 * @returns {string[] | undefined} The resource with the scopes
 */
function _getScopesAuthRequest(scopes, resource) {
    if (resource) {
        if (scopes && scopes.length !== 0) {
            return scopes.map((scp) => resource + '/' + scp);
        }
        return [resource];
    }
    return scopes;
}
/**
 *
 * @param {Router} router The angular router
 * @returns { () => Promise<void>} Callback promise
 */
function addCallbackRoute(router) {
    const callbackRoute = {
        path: M_CALLBACK_ROUTE,
        component: MAuthCallbackComponent
    };
    router.config.unshift(callbackRoute);
    return () => Promise.resolve();
}

class MHasPermissionDirective {
    constructor(templateRef, viewContainer, mAuthService) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.mAuthService = mAuthService;
        this.roles = [];
        this.hasPermissionSubject = new Subject();
        this.hasPermission$ = this.hasPermissionSubject.asObservable();
        this.destroy$ = new Subject();
        this.elseTemplateRef = null;
    }
    set mHasPermission(roles) {
        this.roles = coerceStringArray(roles);
        this.hasPermissionSubject.next();
    }
    set mHasPermissionIn(scope) {
        this.scope = scope;
        this.hasPermissionSubject.next();
    }
    set mHasPermissionElse(templateRef) {
        this.elseTemplateRef = templateRef;
    }
    ngOnInit() {
        this.hasPermission$
            .pipe(switchMap$1(() => this.checkPermissions()), takeUntil(this.destroy$))
            .subscribe();
        this.hasPermissionSubject.next();
        this.mAuthService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.hasPermissionSubject.next();
        });
    }
    checkPermissions() {
        return this.mAuthService.hasPermission(this.roles, this.scope).pipe(tap((hasPermission) => {
            if (hasPermission) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            }
            else {
                if (this.elseTemplateRef) {
                    this.viewContainer.createEmbeddedView(this.elseTemplateRef);
                }
                else {
                    this.viewContainer.clear();
                }
            }
        }));
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
MHasPermissionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MHasPermissionDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: MAuthService }], target: i0.ɵɵFactoryTarget.Directive });
MHasPermissionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MHasPermissionDirective, isStandalone: true, selector: "[mHasPermission]", inputs: { mHasPermission: "mHasPermission", mHasPermissionIn: "mHasPermissionIn", mHasPermissionElse: "mHasPermissionElse" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MHasPermissionDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[mHasPermission]',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: MAuthService }]; }, propDecorators: { mHasPermission: [{
                type: Input
            }], mHasPermissionIn: [{
                type: Input
            }], mHasPermissionElse: [{
                type: Input
            }] } });

class MHasSomePermissionDirective {
    constructor(templateRef, viewContainer, mAuthService) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.mAuthService = mAuthService;
        this.roles = [];
        this.hasSomePermissionSubject = new Subject();
        this.hasSomePermission$ = this.hasSomePermissionSubject.asObservable();
        this.destroy$ = new Subject();
        this.elseTemplateRef = null;
    }
    set mHasSomePermission(roles) {
        this.roles = coerceStringArray(roles);
        this.hasSomePermissionSubject.next();
    }
    set mHasSomePermissionIn(scope) {
        this.scope = scope;
        this.hasSomePermissionSubject.next();
    }
    set mHasSomePermissionElse(templateRef) {
        this.elseTemplateRef = templateRef;
    }
    ngOnInit() {
        this.hasSomePermission$
            .pipe(switchMap$1(() => this.checkPermissions()), takeUntil(this.destroy$))
            .subscribe();
        this.hasSomePermissionSubject.next();
        this.mAuthService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.hasSomePermissionSubject.next();
        });
    }
    checkPermissions() {
        return this.mAuthService.hasSomePermission(this.roles, this.scope).pipe(tap((hasPermission) => {
            if (hasPermission) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            }
            else {
                if (this.elseTemplateRef) {
                    this.viewContainer.createEmbeddedView(this.elseTemplateRef);
                }
                else {
                    this.viewContainer.clear();
                }
            }
        }));
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
MHasSomePermissionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MHasSomePermissionDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: MAuthService }], target: i0.ɵɵFactoryTarget.Directive });
MHasSomePermissionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MHasSomePermissionDirective, isStandalone: true, selector: "[mHasSomePermission]", inputs: { mHasSomePermission: "mHasSomePermission", mHasSomePermissionIn: "mHasSomePermissionIn", mHasSomePermissionElse: "mHasSomePermissionElse" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MHasSomePermissionDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[mHasSomePermission]',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: MAuthService }]; }, propDecorators: { mHasSomePermission: [{
                type: Input
            }], mHasSomePermissionIn: [{
                type: Input
            }], mHasSomePermissionElse: [{
                type: Input
            }] } });

class MAuthRoleGuard {
    constructor() {
        this.mAuthService = inject(MAuthService);
    }
    canActivateChild(route) {
        return this.checkIfIsAuthenticated(route.data);
    }
    canActivate(route) {
        return this.checkIfIsAuthenticated(route.data);
    }
    canMatch(route) {
        return this.checkIfIsAuthenticated(route.data);
    }
    /**
     * Checks if user is authenticated by means of roles and/or scope
     *
     * @param {Data | undefined} data The router data
     * @returns {boolean | Observable<boolean>} A boolean or boolean observable that determinates if user is authenticated
     */
    checkIfIsAuthenticated(data) {
        const roles = data?.roles;
        const scope = data?.scope;
        if (!roles) {
            return true;
        }
        return this.mAuthService.isAuthenticated$.pipe(switchMap$1((isAuthenticated) => {
            if (isAuthenticated) {
                return this.mAuthService.hasPermission(roles, scope);
            }
            this.mAuthService.login();
            return of(false);
        }));
    }
}
MAuthRoleGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthRoleGuard, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MAuthRoleGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthRoleGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthRoleGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class MAuthSomeRoleGuard {
    constructor() {
        this.mAuthService = inject(MAuthService);
    }
    canActivateChild(route) {
        return this.checkIfIsAuthenticated(route.data);
    }
    canActivate(route) {
        return this.checkIfIsAuthenticated(route.data);
    }
    canMatch(route) {
        return this.checkIfIsAuthenticated(route.data);
    }
    /**
     * Checks if user is authenticated by means of roles and/or scope
     *
     * @param {Data | undefined} data The router data
     * @returns {boolean | Observable<boolean>} A boolean or boolean observable that determinates if user is authenticated
     */
    checkIfIsAuthenticated(data) {
        const roles = data?.roles;
        const scope = data?.scope;
        if (!roles) {
            return of(true);
        }
        return this.mAuthService.isAuthenticated$.pipe(switchMap$1((isAuthenticated) => {
            if (isAuthenticated) {
                return this.mAuthService.hasSomePermission(roles, scope);
            }
            this.mAuthService.login();
            return of(false);
        }));
    }
}
MAuthSomeRoleGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthSomeRoleGuard, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MAuthSomeRoleGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthSomeRoleGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthSomeRoleGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Guard for routes to avoid unauthenticated user enter.
 * Can protect to active or load ona route or activate a the child routes.
 */
class MAuthGuard extends MsalGuard {
    constructor(msalGuardConfig, msalBroadcastService, authService, location, router, mAuth) {
        super(msalGuardConfig, msalBroadcastService, authService, location, router);
        this.mAuth = mAuth;
    }
    canLoad() {
        return super.canLoad().pipe(tap$1((res) => {
            if (!res && !this._hasError()) {
                this.mAuth.login();
            }
        }));
    }
    _hasError() {
        return Boolean(location.pathname.includes('/error') || location.search?.includes('error') || location.hash.includes('error'));
    }
}
MAuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthGuard, deps: [{ token: MSAL_GUARD_CONFIG }, { token: i1.MsalBroadcastService }, { token: i1.MsalService }, { token: i3.Location }, { token: i2.Router }, { token: MAuthService }], target: i0.ɵɵFactoryTarget.Injectable });
MAuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MSAL_GUARD_CONFIG]
                }] }, { type: i1.MsalBroadcastService }, { type: i1.MsalService }, { type: i3.Location }, { type: i2.Router }, { type: MAuthService }]; } });

class MAuthModule {
    /**
     * @description
     * Provides options for configuring the MAuthModule in root mode.
     * @interface MAuthConfig
     * @param {MAuthConfig} moduleConfig An object of configuration options
     * @returns {ModuleWithProviders<MAuthModule>} The module with providers
     */
    static forRoot(moduleConfig) {
        return {
            ngModule: MAuthModule,
            providers: [
                {
                    provide: APP_INITIALIZER,
                    useFactory: addCallbackRoute,
                    deps: [Router],
                    multi: true
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: () => {
                        const service = inject(MAuthService);
                        const broadcastService = inject(MsalBroadcastService);
                        const msalService = inject(MsalService);
                        const location = inject(LOCATION);
                        if (moduleConfig.authOnAppStart) {
                            return () => broadcastService.msalSubject$.pipe(first(), switchMap$1((status) => {
                                if (status.eventType === EventType.LOGIN_SUCCESS ||
                                    status.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                                    status.eventType === EventType.LOGIN_FAILURE ||
                                    Boolean(msalService.instance.getActiveAccount()?.username) ||
                                    location.pathname.includes('/error') ||
                                    location.search?.includes('error') ||
                                    location.hash.includes('error')) {
                                    return EMPTY;
                                }
                                return service.login();
                            }));
                        }
                        return () => Promise.resolve();
                    },
                    multi: true
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MsalInterceptor,
                    multi: true
                },
                {
                    provide: M_AUTH_CONFIGURATION,
                    useValue: moduleConfig
                },
                {
                    provide: M_AUTH_INJECTION_LEVEL,
                    useValue: 'root'
                },
                {
                    provide: MSAL_INSTANCE,
                    useFactory: msalInstanceFactory,
                    deps: [M_AUTH_CONFIGURATION]
                },
                {
                    provide: MSAL_GUARD_CONFIG,
                    useFactory: msalGuardConfigFactory,
                    deps: [M_AUTH_CONFIGURATION]
                },
                {
                    provide: MSAL_INTERCEPTOR_CONFIG,
                    useFactory: msalInterceptorConfigFactory,
                    deps: [M_AUTH_CONFIGURATION]
                },
                MAuthGuard,
                MAuthRoleGuard,
                MAuthSomeRoleGuard,
                MsalBroadcastService,
                MsalService,
                MAuthService
            ]
        };
    }
    /**
     * @description
     * Provides options for configuring the MAuthModule in child mode.
     * @interface MAuthConfig
     * @param {MAuthConfig} moduleConfig An object of configuration options
     * @returns {ModuleWithProviders<MAuthModule>} The module with providers
     */
    static forChild(moduleConfig) {
        return {
            ngModule: MAuthModule,
            providers: [
                {
                    provide: MSAL_INTERCEPTOR_CONFIG,
                    useFactory: msalInterceptorConfigFactory,
                    deps: [M_AUTH_CONFIGURATION]
                },
                {
                    provide: HttpClient,
                    useClass: HttpClient
                },
                {
                    provide: HttpHandler,
                    useClass: ɵHttpInterceptingHandler
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MsalInterceptor,
                    multi: true
                },
                {
                    provide: ENVIRONMENT_INITIALIZER,
                    useFactory: () => {
                        const service = inject(MAuthService);
                        const broadcastService = inject(MsalBroadcastService);
                        if (moduleConfig.authOnAppStart) {
                            // Instead of returning an observable, with this token we have to fire some function
                            return () => broadcastService.msalSubject$
                                .pipe(first(), switchMap$1((status) => {
                                if (status.eventType === EventType.LOGIN_SUCCESS ||
                                    status.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
                                    return EMPTY;
                                }
                                return service.login();
                            }))
                                .subscribe();
                        }
                        return () => Promise.resolve();
                    },
                    multi: true
                },
                {
                    provide: M_AUTH_CONFIGURATION,
                    useValue: moduleConfig
                },
                {
                    provide: M_AUTH_INJECTION_LEVEL,
                    useValue: 'child'
                },
                {
                    provide: MSAL_INSTANCE,
                    useFactory: msalInstanceFactory,
                    deps: [M_AUTH_CONFIGURATION]
                },
                {
                    provide: MSAL_GUARD_CONFIG,
                    useFactory: msalGuardConfigFactory,
                    deps: [M_AUTH_CONFIGURATION]
                },
                MAuthGuard,
                MAuthRoleGuard,
                MAuthSomeRoleGuard,
                MsalBroadcastService,
                MsalService,
                MAuthService
            ]
        };
    }
}
MAuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MAuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MAuthModule, imports: [CommonModule, MsalModule, MHasPermissionDirective, MHasSomePermissionDirective], exports: [MHasPermissionDirective, MHasSomePermissionDirective] });
MAuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthModule, imports: [CommonModule, MsalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MsalModule, MHasPermissionDirective, MHasSomePermissionDirective],
                    exports: [MHasPermissionDirective, MHasSomePermissionDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MAuthGuard, MAuthModule, MAuthRoleGuard, MAuthService, MAuthSomeRoleGuard, MHasPermissionDirective, MHasSomePermissionDirective, M_AUTH_CONFIGURATION, M_AUTH_INJECTION_LEVEL, M_CALLBACK_ROUTE, decodeToken, generateCodeChallenge, generateRandomString, validateToken };
